import React from 'react';

/**
 * Messages browsers emit when a lazily-imported chunk cannot be fetched.
 *
 * Observed in Datadog RUM (FRONTEND-690) after a release changed the content
 * hash of lazy chunks while users still had an older page open: the stale page
 * requests the old hashed filename, which no longer resolves.
 *
 * This list is deliberately narrow. A missed match falls through to the generic
 * error screen, which is today's behaviour and therefore no regression. A false
 * match is worse: it would tell a user to reload when the real problem is a
 * code defect that reloading cannot fix.
 */
const STALE_CHUNK_MESSAGE_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /importing a module script failed/i,
  /unable to preload css/i,
  /cross-origin script load denied/i,
];

/**
 * Marker set on errors that came from a failed chunk fetch, so the error
 * boundary can tell them apart from ordinary render errors.
 *
 * The original error is re-thrown with this flag attached rather than being
 * replaced, so the message, stack and chunk URL still reach Datadog and Sentry
 * unchanged.
 */
const STALE_CHUNK_ERROR_FLAG = '__alloyStaleChunkError';

export const STALE_CHUNK_RELOAD_STORAGE_KEY = 'alloy:stale-chunk-reload-attempted';

/** Whether an error message looks like a chunk that failed to load. */
export const isStaleChunkMessage = (message: unknown): boolean => (
  typeof message === 'string'
  && STALE_CHUNK_MESSAGE_PATTERNS.some(pattern => pattern.test(message))
);

/**
 * Whether an error was raised by a failed dynamic import, as opposed to an
 * error thrown while rendering a module that loaded successfully.
 *
 * Only errors that passed through {@link lazyWithRetry} are recognised, so a
 * view that throws a similar-looking message of its own (a CORS failure from
 * its own data fetching, say) is not mistaken for a stale bundle.
 */
export const isStaleChunkError = (error: unknown): boolean => (
  typeof error === 'object'
  && error !== null
  && (error as Record<string, unknown>)[STALE_CHUNK_ERROR_FLAG] === true
);

/**
 * Flags an error as a stale-chunk failure if its message says so, and returns
 * the same error object so the message, stack and chunk URL are preserved.
 *
 * The message is the only signal checked — browsers throw a TypeError for a
 * failed import(), but Vite's own CSS preload-helper throws a plain Error, so
 * tagging must not assume either constructor.
 *
 * Exported for testing; prefer {@link lazyWithRetry} at call sites.
 */
export const tagStaleChunkError = (error: unknown): unknown => {
  if (typeof error === 'object' && error !== null && isStaleChunkMessage((error as Error).message)) {
    try {
      (error as Record<string, unknown>)[STALE_CHUNK_ERROR_FLAG] = true;
    } catch {
      // Non-extensible error object; fall through to the generic error screen.
    }
  }

  return error;
};

/**
 * Drop-in replacement for `React.lazy` that tags stale-chunk failures.
 *
 * Behaviour is otherwise identical to `React.lazy`: the error is still thrown
 * and still reaches the nearest error boundary. The boundary uses
 * {@link isStaleChunkError} to decide whether to auto-reload or show a
 * "new version available" state instead of the generic crash screen.
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() => importFn().catch((error: unknown) => {
    throw tagStaleChunkError(error);
  }));
}

/**
 * Reloads the page once per session for a stale-chunk failure, guarding
 * against a reload loop if the new bundle keeps failing for some other reason.
 *
 * Returns whether it actually reloaded, so callers can fall back to an
 * actionable error state instead of reloading again.
 */
export function attemptStaleChunkReload(location: { reload: () => void }): boolean {
  try {
    if (sessionStorage.getItem(STALE_CHUNK_RELOAD_STORAGE_KEY) === 'true') {
      return false;
    }

    sessionStorage.setItem(STALE_CHUNK_RELOAD_STORAGE_KEY, 'true');
  } catch {
    // sessionStorage inaccessible (e.g. private browsing) — don't reload blindly.
    return false;
  }

  location.reload();
  return true;
}
